import {
  ENVELOPE_STATUSES,
  type EnvelopeStatus,
  PARTY_TYPES,
  type PartyType,
  SUBJECT_TYPES,
  type SubjectType
} from "../types/esign.types.ts"
import {
  EMPTY_FILTERS,
  TILE_IDS,
  type TileId,
  TRACKING_ORDERINGS,
  type TrackingFilters,
  type TrackingOrdering,
  type TrackingQuery
} from "../types/tracking.types.ts"

const LISTABLE_STATUSES = ENVELOPE_STATUSES.filter(
  (status) => status !== "draft"
)
const LIST_KEYS = ["status", "document_type", "party_type", "sent_by"] as const
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const DAY = /^\d{4}-\d{2}-\d{2}$/

const listOf = <T extends string>(
  raw: string | null,
  allowed: (value: string) => value is T
): T[] =>
  (raw ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter(allowed)

const oneOf =
  <T extends string>(values: readonly T[]) =>
  (value: string): value is T =>
    (values as readonly string[]).includes(value)

/** What the URL asks for, dropping anything the API would refuse. */
export const filtersFromSearch = (search: URLSearchParams): TrackingFilters => {
  const tile = search.get("tile")
  const ordering = search.get("ordering")
  const day = (key: string) => {
    const value = search.get(key)
    return value && DAY.test(value) ? value : null
  }
  return {
    status: listOf<EnvelopeStatus>(
      search.get("status"),
      oneOf(LISTABLE_STATUSES)
    ),
    document_type: listOf<SubjectType>(
      search.get("document_type"),
      oneOf(SUBJECT_TYPES)
    ),
    party_type: listOf<PartyType>(search.get("party_type"), oneOf(PARTY_TYPES)),
    sent_by: listOf<string>(search.get("sent_by"), (value): value is string =>
      UUID.test(value)
    ),
    sent_after: day("sent_after"),
    sent_before: day("sent_before"),
    tile: tile && oneOf(TILE_IDS)(tile) ? (tile as TileId) : null,
    q: search.get("q") ?? "",
    ordering:
      ordering && oneOf(TRACKING_ORDERINGS)(ordering)
        ? (ordering as TrackingOrdering)
        : null
  }
}

export const queryFromFilters = (filters: TrackingFilters): TrackingQuery => {
  const query: TrackingQuery = {}
  for (const key of LIST_KEYS) {
    if (filters[key].length) query[key] = filters[key].join(",")
  }
  for (const key of [
    "sent_after",
    "sent_before",
    "tile",
    "ordering"
  ] as const) {
    if (filters[key]) query[key] = filters[key] as string
  }
  if (filters.q.trim()) query.q = filters.q.trim()
  return query
}

export const searchFromFilters = (filters: TrackingFilters): URLSearchParams =>
  new URLSearchParams(queryFromFilters(filters) as Record<string, string>)

/** How many filters narrow the table — the order does not. */
export const activeFilterCount = (filters: TrackingFilters): number =>
  (Object.keys(EMPTY_FILTERS) as Array<keyof TrackingFilters>).filter((key) => {
    if (key === "ordering") return false
    const value = filters[key]
    return Array.isArray(value) ? value.length > 0 : Boolean(value)
  }).length
